// src/sectors/pilates/settings/tabs/sections/company/PlansSection.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { PlanListSection } from '../../plans/components/PlanListSection';
import { useActivities } from 'sectors/pilates/settings/activities/hooks/useActivities';
import { PlanBuilder } from 'sectors/pilates/members/modal/MemberCreateModal/parts/plan/PlanBuilder';
import { pilatesSettingsApi } from '../../pilatesSettingsApi';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';

export function CompanyPackagesSection({ loading, plans, onReload }) {
  const theme = useMuiTheme();
  const list = Array.isArray(plans) ? plans : [];

  // ---- shared compact tokens (match activities) ----
  const TITLE_SX = { fontWeight: 900, fontSize: 13, lineHeight: 1.1 };
  const ERR_SX = { color: 'error.main', fontWeight: 900 };
  const iconBtnSx = { p: 0.6, borderRadius: '12px' };

  // builder dialog state
  const [builderOpen, setBuilderOpen] = React.useState(false);
  const [builderMode, setBuilderMode] = React.useState('CREATE'); // CREATE | EDIT
  const [editingPlanId, setEditingPlanId] = React.useState(null);
  const acts = useActivities({ open: builderOpen });

  // activities
  const activeActivities = React.useMemo(
    () => (acts.items || []).filter((a) => a?.active !== false),
    [acts.items]
  );
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const openCreate = () => {
    setErr(null);
    setBuilderMode('CREATE');
    setEditingPlanId(null);
    setBuilderOpen(true);
  };

  const openEdit = (id) => {
    setErr(null);
    setBuilderMode('EDIT');
    setEditingPlanId(String(id));
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    if (busy) return;
    setBuilderOpen(false);
    setEditingPlanId(null);
    setBuilderMode('CREATE');
  };

  // ✅ REAL: CREATE -> POST
  const createPlan = async (payload) => {
    if (busy) return null;
    setBusy(true);
    setErr(null);
    try {
      const created = await pilatesSettingsApi.createPlan(payload);
      await onReload?.();
      return created;
    } catch (e) {
      console.error(e);
      setErr('Paket kaydedilemedi.');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // ✅ REAL: UPDATE -> PUT
  const updatePlan = async (planId, payload) => {
    if (busy) return null;
    setBusy(true);
    setErr(null);
    try {
      const updated = await pilatesSettingsApi.updatePlan(planId, payload);
      await onReload?.();
      return updated;
    } catch (e) {
      console.error(e);
      setErr('Paket güncellenemedi.');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // ✅ REAL: ACTIVE/PASSIVE
  const toggleActive = async (id, nextActive) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await pilatesSettingsApi.setPlanActive(id, nextActive);
      await onReload?.();
    } catch (e) {
      console.error(e);
      setErr(nextActive ? 'Paket aktifleştirilemedi.' : 'Paket pasife alınamadı.');
    } finally {
      setBusy(false);
    }
  };

  const handleDone = (res) => {
    if (res?.type === 'LIBRARY' || res?.type === 'LIBRARY_EDITED') {
      setBuilderOpen(false);
      setEditingPlanId(null);
      setBuilderMode('CREATE');
    }
  };

  const softDialogPaper = React.useMemo(
    () => ({
      borderRadius: '20px',
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[10],
      background:
        theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, rgba(26,28,36,.92), rgba(18,20,26,.96))'
          : 'linear-gradient(180deg, rgba(255,255,255,.96), rgba(250,250,252,.98))',
    }),
    [theme]
  );

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingToggle, setPendingToggle] = React.useState(null);

  const getPlanName = React.useCallback(
    (id) => list.find((p) => String(p.id) === String(id))?.name || 'Paket',
    [list]
  );

  const askToggleActive = React.useCallback(
    (id, nextActive) => {
      setErr(null);
      setPendingToggle({
        id,
        nextActive,
        name: getPlanName(id),
      });
      setConfirmOpen(true);
    },
    [getPlanName]
  );

  return (
    <MDBox sx={{ display: 'grid', gap: 1 }}>
      {err ? (
        <MDTypography variant="caption" sx={ERR_SX}>
          {err}
        </MDTypography>
      ) : null}

      <PlanListSection
        plansLoading={loading || busy}
        plans={list}
        onOpenCreateModal={openCreate}
        onEditPlan={openEdit}
        onToggleActive={askToggleActive}
      />

      <Dialog
        open={builderOpen}
        onClose={closeBuilder}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: softDialogPaper }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon
                sx={{
                  fontSize: 20,
                  color:
                    builderMode === 'EDIT' ? theme.palette.info.main : theme.palette.success.main,
                }}
              >
                construction
              </Icon>
              <MDTypography sx={TITLE_SX}>
                {builderMode === 'EDIT' ? 'Planı Düzenle' : 'Plan Oluştur'}
              </MDTypography>
            </MDBox>

            <IconButton sx={iconBtnSx} size="small" onClick={closeBuilder} disabled={busy}>
              <Icon sx={{ color: theme.palette.text.secondary }}>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <PlanBuilder
            mode={builderMode}
            initialPlan={
              builderMode === 'EDIT'
                ? list.find((p) => String(p.id) === String(editingPlanId))
                : null
            }
            editingPlanId={editingPlanId}
            activities={activeActivities}
            onCreateActivity={async (name) => {
              await acts.create?.({ name });
              await acts.fetchList?.();
            }}
            onUpdateActivity={async (id, patch) => {
              await acts.update?.(id, patch);
              await acts.fetchList?.();
            }}
            onDeleteActivity={async (id) => {
              await acts.setActive?.(id, false);
              await acts.fetchList?.();
            }}
            onSave={createPlan}
            onUpdate={updatePlan}
            onDone={handleDone}
            onCloseBuilder={closeBuilder}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        tone={pendingToggle?.nextActive ? 'success' : 'warning'}
        title={
          pendingToggle?.nextActive ? 'Paket aktifleştirilsin mi?' : 'Paket pasife alınsın mı?'
        }
        subtitle={
          pendingToggle?.nextActive
            ? 'Bu paket tekrar kullanılabilir olacak.'
            : 'Bu paket yeni satışlarda seçilemeyecek.'
        }
        message={() =>
          pendingToggle?.nextActive
            ? `“${pendingToggle?.name || 'Paket'}” aktifleştirilecek.\nDevam etmek istiyor musun?`
            : `“${pendingToggle?.name || 'Paket'}” pasife alınacak.\nDevam etmek istiyor musun?`
        }
        confirmText={pendingToggle?.nextActive ? 'Aktifleştir' : 'Pasife Al'}
        cancelText="Vazgeç"
        busy={busy}
        onCancel={() => {
          if (busy) return;
          setConfirmOpen(false);
          setPendingToggle(null);
        }}
        onConfirm={async () => {
          if (!pendingToggle?.id) return;
          await toggleActive(pendingToggle.id, pendingToggle.nextActive);
          setConfirmOpen(false);
          setPendingToggle(null);
        }}
      />
    </MDBox>
  );
}
